import { type StateBuilder } from '@canvas/state-machine/builder/types';
import { keep } from '@canvas/state-machine/common/context';
import { always } from '@canvas/state-machine/common/guards';

import { asRejectEvent, asyncState } from '../../async-state';
import { type State } from '../../network-state-machine';
import {
  generateTraceId,
  type SocketMachineContext,
} from '../../network-state-machine-context';
import { type Events, type EventsLookup } from '../../network-state-machine-events';
import { connectingState, type ConnectingStates } from '../connecting/connecting-state';

import { onExitActive } from './active-effects';
import {
  isPossibleToRetry,
  isSessionRevoked,
  resetDebounce,
} from './active-state-context';
import { closedAsync } from './closed/closed-effects';
import {
  onEnterConnected,
  onReEnterConnected,
  onExitConnected,
} from './connected/connected-effects';

export type ActiveStates =
  | 'active'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | `connecting:${ConnectingStates}`
  | 'closed';

export function activeState(
  builder: StateBuilder<State, Events, EventsLookup, SocketMachineContext>
): StateBuilder<State, Events, EventsLookup, SocketMachineContext> {
  return builder
    .state('connecting', connectingState)
    .state('connected', (s) =>
      s
        .when('close', (t) => [
          t.if(isSessionRevoked).context(resetDebounce).to('closed'),
          t.if(isPossibleToRetry).context(keep).to('closed'),
          t.if(always).context(keep).to('destroyed'),
        ])
        .when('error', (t) => t.if(always).context(keep).to('closed'))
        .when('restart', (t) => t.if(always).context(keep).to('closed'))
        .when('send', (t) => t.if(always).context(keep).to('connected'))
        .on('enter', onEnterConnected)
        .on('re-enter', onReEnterConnected)
        .on('exit', onExitConnected)
    )
    .state(
      'closed',
      asyncState(closedAsync, asRejectEvent, (s) =>
        s.when('reconnect', (t) =>
          t.if(always).context(generateTraceId).to('connecting:waiting')
        )
      )
    )
    .when('abort', (t) => t.if(always).context(keep).to('destroyed'))
    .on('exit', onExitActive);
}
